import React, { useState } from 'react';
import { PRODUCT_STATES, DISCOUNT_CATEGORY_EMPTY } from '../constants';
import { readProductListQuery } from '../productListQuery';
import MobileProductDialog from './MobileProductDialog';
import MobileProductField from './MobileProductField';
import MobileFilterAutocomplete from './MobileFilterAutocomplete';
import MobileProductFeedback from './MobileProductFeedback';
import { isIntegerAmount } from './mobileProductUtils';

export const mobileFilterCount = query =>
  [
    'productId',
    'search',
    'quantityFrom',
    'quantityTo',
    'priceFrom',
    'priceTo',
    'discountFrom',
    'discountTo',
  ].filter(key => query[key] !== '').length +
  query.state.length +
  query.categoryIds.length +
  query.discountCategoryNames.length +
  (query.sortBy !== 'id' || query.sortOrder !== 'desc' ? 1 : 0);

export default function MobileProductFilters({
  query,
  categories,
  discountCategories,
  resourcesError,
  onRetry,
  onApply,
  onClose,
}) {
  const [draft, setDraft] = useState(query);
  const [error, setError] = useState('');
  const change = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setError('');
  };
  const apply = () => {
    for (const key of [
      'productId',
      'quantityFrom',
      'quantityTo',
      'priceFrom',
      'priceTo',
      'discountFrom',
      'discountTo',
    ]) {
      if (
        draft[key] !== '' &&
        (!isIntegerAmount(draft[key]) || (key === 'productId' && Number(draft[key]) === 0))
      ) {
        setError(
          key === 'productId'
            ? 'ID товару має бути цілим числом, більшим за нуль'
            : 'Кількість і ціна мають бути цілими невід’ємними числами',
        );
        return;
      }
    }
    if (['discountFrom', 'discountTo'].some(key => draft[key] !== '' && Number(draft[key]) > 100)) {
      setError('Знижка має бути від 0 до 100');
      return;
    }
    for (const [from, to] of [
      ['quantityFrom', 'quantityTo'],
      ['priceFrom', 'priceTo'],
      ['discountFrom', 'discountTo'],
    ]) {
      if (draft[from] !== '' && draft[to] !== '' && Number(draft[from]) > Number(draft[to])) {
        setError('Значення «від» не може бути більшим за «до»');
        return;
      }
    }
    const { page, ...filters } = draft;
    onApply(filters);
  };
  return (
    <MobileProductDialog
      size='filters'
      title='Фільтри товарів'
      onClose={onClose}
      onSubmit={apply}
      footer={
        <>
          <button
            type='button'
            className='btn btn-sm btn-outline-secondary'
            onClick={() => {
              setDraft(readProductListQuery(''));
              setError('');
            }}
          >
            Очистити
          </button>
          <button type='submit' className='btn btn-sm btn-dark'>
            Застосувати
          </button>
        </>
      }
    >
      <div className='mp-filters-form'>
        <MobileProductFeedback
          error={error || resourcesError}
          onRetry={resourcesError && onRetry}
        />
        <MobileFilterAutocomplete
          label='Статуси'
          options={PRODUCT_STATES.filter(state => state.value)}
          value={draft.state}
          onChange={value => change('state', value)}
          placeholder='Знайти статус'
        />
        <MobileFilterAutocomplete
          label='Категорії'
          options={categories.map(category => ({ value: category.id, label: category.name }))}
          value={draft.categoryIds}
          onChange={value => change('categoryIds', value)}
          missingLabel={id => `Категорія #${id}`}
          placeholder='Знайти категорію'
        />
        <MobileFilterAutocomplete
          label='Групи знижок'
          options={[
            { value: DISCOUNT_CATEGORY_EMPTY, label: 'Без групи знижок' },
            ...discountCategories.map(category => ({ value: category.name, label: category.name })),
          ]}
          value={draft.discountCategoryNames}
          onChange={value => change('discountCategoryNames', value)}
          placeholder='Знайти групу знижок'
        />
        <fieldset className='mp-filter-range'>
          <legend>Залишок, шт.</legend>
          <div className='mp-field-grid'>
            <MobileProductField
              label='Кількість від'
              type='number'
              min='0'
              inputMode='numeric'
              placeholder='Від'
              value={draft.quantityFrom}
              onChange={value => change('quantityFrom', value)}
            />
            <MobileProductField
              label='Кількість до'
              type='number'
              min='0'
              inputMode='numeric'
              placeholder='До'
              value={draft.quantityTo}
              onChange={value => change('quantityTo', value)}
            />
          </div>
        </fieldset>
        <fieldset className='mp-filter-range'>
          <legend>Ціна, грн</legend>
          <div className='mp-field-grid'>
            <MobileProductField
              label='Ціна від, грн'
              type='number'
              min='0'
              inputMode='numeric'
              placeholder='Від'
              value={draft.priceFrom}
              onChange={value => change('priceFrom', value)}
            />
            <MobileProductField
              label='Ціна до, грн'
              type='number'
              min='0'
              inputMode='numeric'
              placeholder='До'
              value={draft.priceTo}
              onChange={value => change('priceTo', value)}
            />
          </div>
        </fieldset>
        <fieldset className='mp-filter-range'>
          <legend>Знижка, %</legend>
          <div className='mp-field-grid'>
            <MobileProductField
              label='Знижка від, %'
              type='number'
              min='0'
              max='100'
              inputMode='numeric'
              placeholder='Від'
              value={draft.discountFrom}
              onChange={value => change('discountFrom', value)}
            />
            <MobileProductField
              label='Знижка до, %'
              type='number'
              min='0'
              max='100'
              inputMode='numeric'
              placeholder='До'
              value={draft.discountTo}
              onChange={value => change('discountTo', value)}
            />
          </div>
        </fieldset>
        <div className='mp-filter-divider' />
        <div className='mp-field-grid'>
          <MobileProductField label='Сортувати за'>
            <select
              className='form-control'
              value={draft.sortBy}
              onChange={event => change('sortBy', event.target.value)}
            >
              {[
                ['id', 'ID'],
                ['name', 'Назвою'],
                ['price', 'Ціною'],
                ['quantity', 'Кількістю'],
                ['discount', 'Знижкою'],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </MobileProductField>
          <MobileProductField label='Порядок'>
            <select
              className='form-control'
              value={draft.sortOrder}
              onChange={event => change('sortOrder', event.target.value)}
            >
              <option value='desc'>Спадання</option>
              <option value='asc'>Зростання</option>
            </select>
          </MobileProductField>
        </div>
        <MobileProductField
          label='ID товару'
          type='number'
          min='1'
          inputMode='numeric'
          placeholder='Будь-який товар'
          value={draft.productId}
          onChange={value => change('productId', value)}
        />
      </div>
    </MobileProductDialog>
  );
}
